<template>
	<v-dialog v-model="show" max-width="720px" scrollable>
		<v-card v-if="preview" rounded="lg" elevation="2">
			<v-card-title class="d-flex align-center bg-primary text-white">
				<v-icon class="me-2">mdi-receipt-text-outline</v-icon>
				{{ $t("temple.printing.previewReceiptTitle") }}
			</v-card-title>

			<v-card-text class="pa-0">
				<!--
					O recibo entra por `srcdoc` e o PDF por `src` sobre a URL do blob —
					o navegador exibe PDF nativamente, sem biblioteca nenhuma. O iframe
					isola o CSS do recibo (que é de bobina térmica) do resto do app.
				-->
				<!--
					`sandbox="allow-same-origin"` sem `allow-scripts`: o recibo é
					documento para OLHAR, não tem script nenhum, e um `srcdoc` roda na
					mesma origem da aplicação — script que vazasse para cá alcançaria o
					`localStorage`. Mantém-se `allow-same-origin` para o logotipo do
					ORI, no rodapé do recibo, continuar carregando sob a CSP
					(`img-src 'self'`): em origem opaca o `'self'` não casaria com nada
					e a imagem sumiria. Aqui NÃO se imprime — quem imprime é o iframe
					do `printHtmlInIframe`, que tem o seu próprio sandbox.
				-->
				<iframe
					class="print-preview__frame"
					sandbox="allow-same-origin"
					:srcdoc="preview.html"
					:title="$t('temple.printing.previewReceiptTitle')"
				/>
			</v-card-text>

			<v-card-actions class="pa-4">
				<span class="text-caption text-medium-emphasis">
					{{ $t("temple.printing.previewHint") }}
				</span>
				<v-spacer />
				<v-btn variant="text" @click="close">{{ $t("common.close") }}</v-btn>
				<v-btn color="primary" variant="flat" @click="print">
					<v-icon start>mdi-printer</v-icon>
					{{ $t("temple.printing.previewPrint") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { usePrintPreview } from "@/composables/usePrintPreview";
import { useNotification } from "@/composables/useNotification";
import { printHtmlInIframe } from "@/utils/printHtml";

/**
 * Diálogo único de pré-visualização do recibo (UX-027), montado uma vez no
 * `App.vue`. Mostra o MESMO HTML que vai para a impressora — não uma segunda
 * renderização —, que é o que faz a conferência valer alguma coisa.
 *
 * O botão de imprimir mora aqui dentro, e não na tela que abriu: é o que
 * garante que ninguém gaste papel sem ter visto o que vai sair.
 */
const { preview, closePrintPreview } = usePrintPreview();
const { t } = useI18n();
const { error: showError } = useNotification();

const show = computed({
	get: () => preview.value !== null,
	set: (value: boolean) => {
		if (!value) closePrintPreview();
	},
});

const close = () => closePrintPreview();

// Este diálogo é montado no `App.vue` FORA da condição de autenticação — é o
// que permite os pontos que imprimem só chamarem o composable. O preço é que
// nada o fecharia ao sair: recibo e relatório do mês ficariam na tela por cima
// do login, com o botão de baixar o PDF funcionando para quem sentasse no
// computador em seguida. A observação mora aqui, e não no `logout()` da store,
// para não criar import circular (o composable já lê a store).
const authStore = useAuthStore();
watch(
	() => authStore.isLoggedIn,
	(logado) => {
		if (!logado) closePrintPreview();
	},
);

const print = () => {
	const html = preview.value?.html;
	if (!html) return;

	// O diálogo NÃO fecha aqui, e o `onFailure` é obrigatório.
	//
	// A primeira versão chamava `printHtmlInIframe(html)` sem callback e fechava
	// no mesmo tique. O `print()` de verdade acontece depois, fora do clique: se
	// o navegador recusasse, a tela já tinha sumido, o recibo não estava mais
	// disponível para tentar de novo, e nada avisava. Era o UX-027 renascendo
	// dentro do conserto do UX-027 — e no caminho PADRÃO, que é o que todo
	// terreiro vai usar.
	//
	// Ficando aberto, quem imprimiu confere o papel e fecha (ou tira uma 2ª via)
	// por conta própria. Fechar sozinho só seria honesto se houvesse prova de
	// que o papel saiu, e não há.
	const falhou = () => showError(t("temple.printing.previewPrintFailed"));
	if (!printHtmlInIframe(html, falhou)) falhou();
};

</script>

<style lang="scss" scoped>
.print-preview__frame {
	width: 100%;
	height: 60vh;
	border: none;
	background: #fff;

}
</style>
